import React, { useState } from 'react';

// Simple inline stubs for UI components
const Icon = ({ name, size = 16, className = '' }) => {
  const sizeStr = size + 'px';
  return <span className={className} style={{ display: 'inline-flex', width: sizeStr, height: sizeStr }} />;
};
const Button = ({ children, onClick, variant, disabled, className = '', type, loading, iconName, ...rest }) => (
  <button type={type || 'button'} onClick={onClick} disabled={disabled || loading} className={`px-4 py-2 rounded-lg font-medium transition-colors ${variant === 'outline' ? 'border border-border bg-card text-gray-700 hover:bg-gray-50' : 'bg-blue-600 text-white hover:bg-blue-700'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`} {...rest}>{loading ? 'Loading...' : children}</button>
);
const Select = ({ value, onChange, options, placeholder, className = '', label, error, required, disabled }) => (
  <div>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required && ' *'}</label>}
    <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled} className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus-visible:outline-none bg-card ${error ? 'border-red-500' : ''} ${className}`}>
      {placeholder && <option value="">{placeholder}</option>}
      {options?.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

const ExportTasksModal = ({ isOpen, onClose, project }) => {
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [includeOptions, setIncludeOptions] = useState({
    assignees: true,
    deadlines: true,
    priorities: true,
    status: true
  });
  const [isExporting, setIsExporting] = useState(false);

  const handleCheckboxChange = (option) => {
    setIncludeOptions(prev => ({
      ...prev,
      [option]: !prev?.[option]
    }));
  };

  const handleExport = async () => {
    setIsExporting(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));

      console.log('Exporting tasks for project:', project?.id);
      console.log('Format:', exportFormat);
      console.log('Include options:', includeOptions);

      onClose();
    } catch (error) {
      console.error('Error exporting tasks:', error);
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Icon name="Download" size={24} className="mr-2 text-primary" />
              Export Tasks
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isExporting}
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Project Info */}
          {project && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-900 mb-1">Project</p>
              <p className="text-lg font-semibold text-blue-900">{project?.name}</p>
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-blue-700">ID: {project?.id}</p>
                <p className="text-xs text-blue-700">{project?.taskCount} tasks</p>
              </div>
            </div>
          )}

          {/* Export Format */}
          <div>
            <Select
              label="Export Format"
              value={exportFormat}
              onChange={setExportFormat}
              options={[
                { value: 'xlsx', label: 'Excel (.xlsx)' },
                { value: 'csv', label: 'CSV (.csv)' }
              ]}
              disabled={isExporting}
            />
          </div>

          {/* Include Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Include in Export
            </label>
            <div className="space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeOptions?.assignees}
                  onChange={() => handleCheckboxChange('assignees')}
                  className="rounded border-border text-primary focus:ring-2 focus:ring-primary"
                  disabled={isExporting}
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Assignee Information</p>
                  <p className="text-xs text-gray-500">Include assigned team member details</p>
                </div>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeOptions?.deadlines}
                  onChange={() => handleCheckboxChange('deadlines')}
                  className="rounded border-border text-primary focus:ring-2 focus:ring-primary"
                  disabled={isExporting}
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Deadlines</p>
                  <p className="text-xs text-gray-500">Include task deadline information</p>
                </div>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeOptions?.priorities}
                  onChange={() => handleCheckboxChange('priorities')}
                  className="rounded border-border text-primary focus:ring-2 focus:ring-primary"
                  disabled={isExporting}
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Priority Levels</p>
                  <p className="text-xs text-gray-500">Include task priority classifications</p>
                </div>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeOptions?.status}
                  onChange={() => handleCheckboxChange('status')}
                  className="rounded border-border text-primary focus:ring-2 focus:ring-primary"
                  disabled={isExporting}
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Task Status</p>
                  <p className="text-xs text-gray-500">Include current status of each task</p>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-border px-6 py-4 flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            loading={isExporting}
            iconName="Download"
          >
            Export
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportTasksModal;
